import React from 'react';
import { 
  Button, 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  Divider, 
  CircularProgress,
  useTheme 
} from '@mui/material';
import { FormData } from '../types';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

interface ConfirmationStepProps {
  data: Partial<FormData>;
  onBack: () => void;
  onSubmit: () => void;
  isSubmitting: boolean;
}

export const ConfirmationStep: React.FC<ConfirmationStepProps> = ({
  data,
  onBack,
  onSubmit,
  isSubmitting,
}) => {
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit();
  };

  // Get preferences with defaults
  const preferences = {
    emailUpdates: data.preferences?.emailUpdates ?? true,
    productRecommendations: data.preferences?.productRecommendations ?? true,
    newsletter: data.preferences?.newsletter ?? false,
  };

  // Format currency for display
  const formatCurrency = (value: string) => {
    if (!value) return 'Not specified';
    const ranges: Record<string, string> = {
      '0-1M': 'Less than $1M',
      '1M-10M': '$1M - $10M',
      '10M-50M': '$10M - $50M',
      '50M-100M': '$50M - $100M',
      '100M+': 'Over $100M'
    };
    return ranges[value] || value;
  };

  const theme = useTheme();

  const SectionHeader = ({ number, title }: { number: number; title: string }) => (
    <Box sx={{ 
      display: 'flex', 
      alignItems: 'center', 
      mb: 3,
      pb: 2,
      borderBottom: '1px solid',
      borderColor: 'divider'
    }}>
      <Box sx={{ 
        width: 32, 
        height: 32, 
        borderRadius: '50%', 
        bgcolor: 'primary.light',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        mr: 2,
        flexShrink: 0
      }}>
        <Typography color="primary" fontWeight={600} variant="body2">
          {number}
        </Typography>
      </Box>
      <Typography variant="h6" component="h3" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
        {title}
      </Typography>
    </Box>
  );

  const InfoCard = ({ children }: { children: React.ReactNode }) => (
    <Box sx={{ 
      p: 2,
      height: '100%',
      bgcolor: 'background.paper',
      borderRadius: 1,
      border: '1px solid',
      borderColor: 'divider',
      boxShadow: theme.shadows[1],
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        boxShadow: theme.shadows[3],
      }
    }}>
      {children}
    </Box>
  );

  const PreferenceItem = ({ 
    label, 
    value, 
    icon: Icon 
  }: { 
    label: string; 
    value: boolean; 
    icon: React.ElementType 
  }) => (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {value ? (
          <CheckCircleIcon color="success" fontSize="small" sx={{ mr: 1 }} />
        ) : (
          <CancelIcon color="disabled" fontSize="small" sx={{ mr: 1 }} />
        )}
        <Typography variant="body2" color={value ? 'success.main' : 'text.secondary'}>
          {value ? 'Enabled' : 'Disabled'}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Typography 
        variant="h5" 
        component="h2" 
        gutterBottom 
        sx={{ 
          fontWeight: 600, 
          mb: 2,
          color: 'primary.main'
        }}
      >
        Review Your Information
      </Typography>
      <Typography 
        variant="body1" 
        color="text.secondary" 
        sx={{ 
          mb: 4,
          maxWidth: '800px'
        }}
      >
        Please verify that all the information below is correct before submitting your application.
      </Typography>

      <Paper 
        variant="outlined" 
        sx={{ 
          p: { xs: 2, md: 4 },
          mb: 4,
          borderRadius: 2,
          borderColor: 'divider',
          bgcolor: 'background.default',
          '&:hover': {
            boxShadow: 2
          }
        }}
      >
        {/* Company Information Section */}
        <Box sx={{ mb: 6 }}>
          <SectionHeader number={1} title="Company Information" />
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <InfoCard>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  COMPANY NAME
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, mb: 3 }}>
                  {data.companyName || 'Not provided'}
                </Typography>
                
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  INDUSTRY
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  {data.industry || 'Not provided'}
                </Typography>
                
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  COUNTRY
                </Typography>
                <Typography variant="body1">
                  {data.country || 'Not provided'}
                </Typography>
              </InfoCard>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <InfoCard>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  COMPANY SIZE
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  {data.companySize || 'Not provided'}
                </Typography>
                
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                  ANNUAL REVENUE
                </Typography>
                <Typography variant="body1" sx={{ mb: data.website ? 3 : 0 }}>
                  {formatCurrency(data.annualRevenue || '')}
                </Typography>
                
                {data.website && (
                  <>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, mt: 3 }}>
                      WEBSITE
                    </Typography>
                    <Box component="span" sx={{ 
                      '& a': {
                        color: 'primary.main',
                        textDecoration: 'none',
                        borderBottom: '1px solid',
                        borderColor: 'transparent',
                        transition: 'border-color 0.2s',
                        '&:hover': {
                          borderColor: 'primary.main'
                        }
                      }
                    }}>
                      <a 
                        href={data.website.startsWith('http') ? data.website : `https://${data.website}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                      >
                        {data.website.replace(/^https?:\/\//, '')}
                      </a>
                    </Box>
                  </>
                )}
              </InfoCard>
            </Grid>
          </Grid>
        </Box>
        
        <Divider sx={{ my: 4 }} />
        
        {/* Preferences Section */}
        <Box sx={{ mb: 4 }}>
          <SectionHeader number={2} title="Notification Preferences" />
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <InfoCard>
                <PreferenceItem 
                  label="EMAIL UPDATES" 
                  value={preferences.emailUpdates} 
                  icon={preferences.emailUpdates ? CheckCircleIcon : CancelIcon} 
                />
                <PreferenceItem 
                  label="PRODUCT RECOMMENDATIONS" 
                  value={preferences.productRecommendations} 
                  icon={preferences.productRecommendations ? CheckCircleIcon : CancelIcon} 
                />
              </InfoCard>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <InfoCard>
                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                  <div>
                    <PreferenceItem 
                      label="NEWSLETTER" 
                      value={preferences.newsletter} 
                      icon={preferences.newsletter ? CheckCircleIcon : CancelIcon} 
                    />
                  </div>
                  
                  <Typography 
                    variant="caption" 
                    color="text.secondary" 
                    sx={{ 
                      mt: 'auto',
                      pt: 2,
                      display: 'block',
                      borderTop: '1px solid',
                      borderColor: 'divider'
                    }}
                  >
                    You can update these preferences at any time in your account settings.
                  </Typography>
                </Box>
              </InfoCard>
            </Grid>
          </Grid>
        </Box>
        
        {/* Submit Section */}
        <Box sx={{ 
          mt: 6,
          pt: 4,
          borderTop: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2
        }}>
          <Button
            variant="outlined"
            onClick={onBack}
            disabled={isSubmitting}
            sx={{ 
              minWidth: 150,
              alignSelf: { xs: 'stretch', sm: 'flex-start' }
            }}
          >
            Back
          </Button>
          
          <Box sx={{ 
            display: 'flex', 
            gap: 2, 
            alignItems: 'center',
            alignSelf: { xs: 'stretch', sm: 'flex-end' }
          }}>
            {isSubmitting && (
              <CircularProgress size={24} />
            )}
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={isSubmitting}
              fullWidth={window.innerWidth < 600}
              sx={{ 
                minWidth: 200, 
                py: 1.5,
                flex: { xs: 1, sm: 'none' }
              }}
            >
              {isSubmitting ? 'Submitting...' : 'Complete Onboarding'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

